import React, { useState } from 'react';
import { updateMenuItem } from '@api';
import { useMenuContext } from '../../hooks';
import {
  styled,
  useTheme,
  Grid,
  Stack,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import SwitchStyled from '@components/SwitchStyled';
import EditPencilIcon from '@components/Icons/EditPencil';
import PrimaryButton from '@components/PrimaryButton';
import ItemOptions from './ItemOptions';
import InputWithLabel from '@components/InputWithLabel';

const ListItemStyled = styled(ListItem, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(1.25),
  marginBottom: theme.spacing(1.25),
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: theme.shape.borderRadius * 4.5,
  borderColor: open ? theme.colors.orange.orange : theme.colors.dark.steelBlue,
}));

const ItemBar = ({ open, setOpen, itemsExist, item, setSelected }) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        width: '100%',
        height: 55.75,
        cursor: 'pointer',
        backgroundColor: theme.colors.light.lightBlue,
        borderRadius: 4.25,
        ...(open && {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }),
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
      onClick={itemsExist ? () => setOpen(!open) : () => setSelected(item.id)}
    >
      {itemsExist && (
        <ListItemIcon sx={{ pl: 1.125, my: 'auto' }}>
          <img
            style={{
              width: 40,
              height: 40,
              borderRadius: '9.33333px',
            }}
            src={item?.icon || ''}
            alt="item icon"
          />
        </ListItemIcon>
      )}
      <ListItemText
        primary={item.name}
        sx={{
          my: 'auto',
          pl: 2,
          textTransform: 'capitalize',
        }}
      />
      <ListItemText
        primary={`€ ${item.priceIncludingTax}`} //TODO: Get/Set proper currency sign from backend or from browser
        sx={{
          my: 'auto',
          pr: 2.375,
          textAlign: 'end',
          color: theme.colors.light.lightCyan,
        }}
      />
    </Stack>
  );
};
const ItemBody = ({ open, setOpen, itemsExist, item }) => {
  const theme = useTheme();
  const currentSession = JSON.parse(localStorage.getItem('session'));
  const { fetchMenuSectionsListForService } = useMenuContext();
  const [selectedTaxRate, setNewTaxRate] = useState(
    item?.priceComponents?.[0].taxRateAmount,
  );
  const [currentState, setCurrentState] = useState({
    ...item,
  });

  const saveItemState = async () => {
    const newState = { ...currentState, product: currentState.product };
    // const newState = { name: currentState.name };
    delete newState.icon;
    // delete newState.priceExcludingTax;
    // delete newState.occasionProfile;
    // delete newState.place;
    // delete newState.productId;
    await updateMenuItem(item.id, newState);
    setOpen(false);
    await fetchMenuSectionsListForService();
  };

  const handleTaxRateChange = value => {
    setNewTaxRate(value);
    setCurrentState({
      ...currentState,
      priceComponent: {
        ...currentState.priceComponents[0],
        taxRateAmount: value,
      },
    });
  };

  const handleChangeInput = ({ target }, label) => {
    const { name } = target;
    if (name === 'shortDescription' || name === 'longDescription') {
      setCurrentState(prevState => ({
        ...prevState,
        product: {
          ...prevState.product,
          [name]: target.value,
        },
      }));
    } else if (name === 'allergens') {
      let newAllergens =
        currentState.product?.allergens?.map(i => i.toLowerCase()) || [];
      const index = newAllergens.indexOf(label.toLowerCase());
      if (index === -1) {
        newAllergens = [...newAllergens, label];
      } else if (index > -1) {
        newAllergens.splice(index, 1);
      }
      console.log({
        [name]: [...newAllergens],
      });
      setCurrentState(prevState => ({
        ...prevState,
        [name]: [...newAllergens],
        product: { [name]: [...newAllergens] },
      }));
    } else if (target.type === 'number') {
      setCurrentState({
        ...currentState,
        [name]: parseFloat(target.value),
        priceComponent: {
          ...currentState.priceComponents[0],
          [name]: parseFloat(target.value),
          taxRateAmount: selectedTaxRate,
        },
      });
    } else {
      setCurrentState({ ...currentState, [name]: target.value });
    }
  };

  const handleSwitch = ({ target: { name, checked } }) => {
    setCurrentState({ ...currentState, [name]: checked });
  };

  return (
    <Collapse in={itemsExist && open} sx={{ width: '100%' }}>
      <Grid
        container
        spacing={2}
        sx={{
          p: 2.5,
          pb: 2,
        }}
      >
        <Grid
          item
          container
          xs={12}
          display="flex"
          direction="row"
          justifyContent="space-between"
          spacing={3.75}
        >
          <Grid item sx={{ position: 'relative', width: 73 }}>
            <IconButton
              onClick={() => {}}
              sx={{
                width: 20,
                height: 20,
                backgroundColor: theme.colors.orange.orange,
                borderRadius: 1.25,
                p: 0,
                '&:hover': { backgroundColor: theme.colors.orange.orange },
                position: 'absolute',
                top: '26.5px',
                right: '-33.5px',
              }}
            >
              <EditPencilIcon />
            </IconButton>
            <img
              style={{
                width: 73,
                height: 73,
                borderRadius: '9.33333px',
              }}
              src={item?.icon || ''}
              alt="item icon"
            />
          </Grid>
          <Grid item sx={{ width: 'calc(100% - 73px - 30px)' }}>
            <InputWithLabel
              label="Product Name"
              placeholder="Product name"
              name="name"
              value={currentState.name}
              handleChangeInput={handleChangeInput}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <InputWithLabel
            label="Subtitle"
            placeholder="Add subtitle"
            name="shortDescription"
            value={currentState?.product?.shortDescription || ''}
            handleChangeInput={handleChangeInput}
          />
        </Grid>
        <Grid item xs={12}>
          <InputWithLabel
            label="description"
            placeholder="Add description"
            name="longDescription"
            value={currentState?.product?.longDescription || ''}
            handleChangeInput={handleChangeInput}
          />
        </Grid>
        <Grid item xs={12}>
          <ItemOptions
            currentState={currentState}
            selectedTaxRate={selectedTaxRate}
            taxRates={currentSession.staff.place.taxRates}
            handleTaxRateChange={handleTaxRateChange}
            handleChangeInput={handleChangeInput}
          />
        </Grid>
        <Grid
          item
          container
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Grid item display="flex" flexDirection="row">
            <Typography
              sx={{
                my: 'auto',
                pr: 2,
              }}
            >
              Product Available
            </Typography>
            <SwitchStyled
              sx={{ my: 'auto' }}
              name="enabled"
              checked={currentState?.enabled}
              onChange={handleSwitch}
            />
          </Grid>
          <Grid item sx={{ my: 'auto', minWidth: 160 }}>
            <PrimaryButton
              text="SAVE"
              handleClick={() => saveItemState()}
              customStyle={{ mb: '0 !important' }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Collapse>
  );
};

const ManageMenuItem = ({ itemsExist, item, setSelected }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ListItemStyled open={open} disablePadding>
        <ItemBar
          open={open}
          setOpen={setOpen}
          itemsExist={itemsExist}
          item={item}
          setSelected={setSelected}
        />
        <ItemBody
          open={open}
          setOpen={setOpen}
          itemsExist={itemsExist}
          item={item}
        />
      </ListItemStyled>
    </>
  );
};

export default ManageMenuItem;
